import { convertToZonedTimestamp } from "common/helpers/dateAndTimeHelpersV2"
import { formatNumber } from "common/helpers/formatNumber"
import { DateButtonWithDropdownContainerProps } from "components/StyledUi/DateRange/DateButtonWithDropdown/DateButtonWithDropdown.elements"
 
import { ScaleBand, scaleBand, ScaleLinear, scaleLinear, ScalePoint, scalePoint, ScaleTime, scaleTime } from "d3"
 import { ReactNode  } from "react"
 
export interface AxisSettingsProps {
  left?: AxisSettingProps,
  right?: AxisSettingProps,
  top?: AxisSettingProps,
  bottom?: AxisSettingProps,
}

export interface ScaleTypesProps {
  line?: ScaleLinear<number, number>,
  time?: ScaleTime<number, number>,
  point?: ScalePoint<string>,
  band?: ScaleBand<string>
}


export type ConfiguredChartScaleTypesProps = keyof ScaleTypesProps
export type ConfiguredChartScaleTypeProps = ScaleTypesProps[ConfiguredChartScaleTypesProps]

export type AxisLocs = keyof AxisSettingsProps

export interface AxisSettingProps {
  key?: string,
  keys?: string[],
  label?: ReactNode,
  format?: (x?: Record<string, unknown>) => ReactNode,
  loc?: AxisLocs,
  labels?: (string | number | Date | ReactNode)[],
  scale_type?: string,
  ticks?: number[],
  width?: number,
  height?: number,
  scale?: ConfiguredChartScaleTypeProps,
  sizer?: { width: number, height: number },
  domain?: (string | number | Date)[]
  range?: [number, number],
  // d3 padding
  padding?: number,
  // d3 align only, 0 to 1, with 0.5 as default, how to align points within their bands, 
  // 0 for left or top alignment, 1 for right or bottom alignment, and 0.5 for centered alignment.
  align?: 0 | 0.5 | 1,
  [key: string]: unknown
}

export interface GenerateDefaultAxisSettingsProps extends Record<string, unknown> {
  domain?: (string | number | DateButtonWithDropdownContainerProps)[]
  axis_settings?: AxisSettingsProps,
  ready_to_size?: boolean,
  // returns true when axis labels are generated
  ready_to_draw?: boolean,
  axis_labels_ready?: boolean,
  sizer?: { width?: number, height?: number },
}
 const getMinMax = (keys: string[] | string, scale_type: string, options?: Record<string, unknown>) => {

  const {
    data,
    stacked = false,
    vertical,
    axis_settings,
    chart_type = ''
  } = (options || {}) as { data?: Record<string, unknown>[], stacked?: boolean, [key: string]: unknown }
  
  if (!Array.isArray(keys)) keys = [keys]
 

  let min = undefined as number | undefined
  let max = undefined as number | undefined
 
  let stack_group_key = undefined as string | undefined
  const stacked_totals = {} as Record<string, number>
 

  const y_key = axis_settings?.left?.key || axis_settings?.right?.key
  const x_key = axis_settings?.top?.key || axis_settings?.bottom?.key
 

  if (stacked) {
    stack_group_key = !vertical ? x_key : y_key
   
  }
 
  for (let i = 0; i < data?.length; i++) {
    const item = data[i]

    keys.forEach((key: string) => { 
      let val = item?.[key]
 
      if (scale_type === 'time') val = new Date(val as string | number  | Date)?.getTime()  
      else val = Number(val || 0)

      if (typeof val !== 'number') return 
 
      if (!stacked) {
        if (min === undefined) min = val || 0
        if (max === undefined) max = val || 0
        
        if (min && val < min) min = val
        if (max && val > max) max = val
      } else {
        
        if (stack_group_key) {
          const stack = String(item?.[stack_group_key as string] || '')
          if (!stacked_totals?.[stack]) stacked_totals[stack] = [0,  0]
          const stack_min = stacked_totals?.[stack]?.[0] || 0
          if (val < stack_min) stacked_totals[stack][0] = val
          stacked_totals[stack][1] += val
        }
      }

    })
  
  }

  if (stacked) {
    Object?.values(stacked_totals)?.forEach((total) => {
      const [stack_min, stack_max] = total as number[]
      
      if (stack_min < (min || 0)) min = stack_min
      if (stack_max > (max || 0)) max = stack_max
    })
 
  }

  if (chart_type === 'bar') min = 0
  return  [min, max]
}


export const generateDefaultAxisSettings = (props?: GenerateDefaultAxisSettingsProps): [AxisSettingsProps | undefined, boolean, boolean] => {
  
  const {
    time_zone = 'UTC',
    chart_settings,
    scale_type,
    data,
    start_time,
    end_time,
    width,
    height,
    value_key,
    value_keys,
    category_key,
    timestamp_key 
  } = (props || {}) as Record<string, unknown & string & (string)[] & Record<string, unknown | string>>

  const chart_type = (chart_settings?.chart_type as string | undefined) || props?.chart_type

  let {
    axis_settings,
    scale_types,
    ready_to_draw = false,
    ready_to_scale = false,
   } = props || {}
 
  const { left, right, top, bottom } = axis_settings || {} 

 
  // some default axis settings based on chart type or scale type, can be overridden by incoming axis settings or domain
  
  if (!scale_types && scale_type) scale_types = { left: 'linear', bottom: (scale_type) as string }
  if (!left && !right)  axis_settings = { ...(axis_settings || {}), left: { key: value_key, keys: value_keys } }
  if (!top && !bottom)  axis_settings = { ...(axis_settings || {}), bottom: { key: category_key || timestamp_key } }
  
  if (!axis_settings) return [{}, ready_to_draw, false] as [AxisSettingsProps & undefined, boolean, boolean]

  // incoming domain overrides axis settings, this way you can control the zoom different ways
  // axis_settings.left = { ...(axis_settings?.left || {}), domain: (axis_settings?.left?.domain || domain) as (string | number | Date)[] }

  // don't use this is if not needed, stick with new format axis_settings.left etc
  if (scale_types) Object?.entries?.(scale_types)?.forEach(([loc, sscale_type]) => {
    if (!axis_settings?.[loc as keyof AxisSettingsProps]) axis_settings = { ...(axis_settings || {}), [loc]: {} as AxisSettingProps }
    axis_settings[loc as keyof AxisSettingsProps] = { 
      scale_type: sscale_type, 
      ...(axis_settings?.[loc as keyof AxisSettingsProps] || {})
    }
  })

  Object.entries?.(axis_settings || {})?.forEach(([loc, axis]) => {

    if (!axis?.scale_type) alert(`missing scale type for ${loc} axis`)
    
    // s?.time s?.band is easier to check for than scale_type === 'time' etc, and allows for custom scale types to 
    // be added to scale_types prop
    // with a custom scaler function
    const s = {
     [axis?.scale_type || '']: true
    } as Record<string, unknown>

    const {
      zoom = true,
      tick_count = ['left', 'right'].includes(loc) ? 4 : 10,
      key,
      keys
    } = axis || {} as AxisSettingProps

    let { 
      domain: new_domain,
      align,
      padding,
      ticks,
      labels,
      scaler,
      scale,
      label,
      range,
      min,
      max,
      start,
      end,
      format
    } = axis || {} as AxisSettingProps
    
    if (!new_domain && start_time && end_time && s?.time) new_domain = [new Date(start_time as string).getTime(), new Date(end_time as string).getTime()]
    if (data) {
      if (s?.point) new_domain = Array.from(new Set((data || [])?.map((d?: Record<string, string | number | unknown>) => d?.[key]))) as (string | number)[]
      else {
        const use_keys = [...(keys ||[]), key].filter((k) => k) as string[]
        
        const minmax = getMinMax(use_keys as string[], axis?.scale_type || '', props) || [] 
        min = min || minmax?.[0] as number
        max = max || minmax?.[1] as number
        if (!zoom || ['bar'].includes(String(chart_type || ''))) min = 0
        if (!new_domain) new_domain = [min, max] as (string | number)[]
      }
    }

    //if (['left', 'right'].includes(loc) && new_domain) new_domain = new_domain.sort((a, b) => Number(b) - Number(a)) as (string | number)[]
 
    if (s?.linear) format = format || ((x?: Record<string, unknown>) => formatNumber(x?.label as number))
    //if (s?.time)  format = format || ((x?: Record<string, unknown>) => convertToZonedTimestamp(x?.label as string | number | Date, time_zone, 'full'))
  
    if (['left', 'right'].includes(loc)) {
      start = height  || 0
      end = 0
    } else {
      start = 0
      end = width || 0
    }
    
    if (chart_type === 'bar') {
      padding = 1
      align = align || .5
    }
    
    range = [start, end]
    //if (width && height) range = ['left', 'right'].includes(loc) ? [height || 0, 0] : [0, width || 0]
    //if (new_domain && ['left', 'right'].includes(loc)) new_domain
    
    scaler =
      s?.linear ? scaleLinear() : 
      s?.time ? scaleTime() : 
      s?.point ? scalePoint() : 
      s?.band ? scaleBand() : 
      undefined

    if (!scaler) {
      ready_to_draw = false
      ready_to_scale = false
      return
    }


    // set the axis scaler
    scale = scaler?.domain(new_domain as (string | number)[])
    // we have stuff to generate an axis, but that doesn't mean we can scale it yet
    ready_to_draw = true      

    if (range) {
      scale = scaler?.domain(new_domain as (string | number)[])?.range(range as [number, number])
      // we have the width and height to scale the axis
      ready_to_scale = true
    }
      
  
    if (s?.linear) ticks = ticks || scale?.ticks(tick_count) as number[]
    if (s?.point) {
      scaler = scaler?.domain(new_domain as string[])?.range(range as [number, number]).align(align).padding(padding)
      ticks = ticks || new_domain as (string | number)[]
    }
    if (s?.band) ticks = ticks || new_domain as (string | number)[]
    if (s?.time) ticks = ticks || scale?.ticks(tick_count)?.map((tick: string | number | Date) => new Date(tick)?.getTime()) as number[]  
    
    if (s?.time) {
      labels = labels || s?.time ?  labels = labels || ticks?.map((tick: string | number | Date) => new Date(tick)?.toISOString()) : labels || ticks
      label = label || `${new Date(new_domain?.[0] as number)?.toLocaleDateString()} - ${new Date(new_domain?.[1] as number)?.toLocaleDateString()}`
    }
    else labels = labels || ticks
    
    if (!axis_settings?.[loc as keyof AxisSettingsProps]) axis_settings = { ...(axis_settings || {}), [loc]: {} as AxisSettingProps }
  
    axis_settings[loc as keyof AxisSettingsProps] = {
      ...axis,
      domain: new_domain,
      scale,
      scaler,
      ticks,
      labels,
      label,
      range,
      min,
      max,
      start,
      end,
      format
    } as AxisSettingProps

    if (labels && ticks) ready_to_draw = true
      
   
    
  })
 
  return [axis_settings as AxisSettingsProps, ready_to_draw, ready_to_scale] as [AxisSettingsProps, boolean, boolean]

}
 